import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useLanguage } from "../contexts/LanguageContext";

function LanguageSelector() {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "pt", name: "Português", flag: "🇧🇷" },
  ];

  const currentLanguage = languages.find((lang) => lang.code === language);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Container ref={containerRef}>
      <SelectorButton onClick={() => setIsOpen(!isOpen)}>
        <Flag>{currentLanguage?.flag}</Flag>
        <LanguageCode>{currentLanguage?.code.toUpperCase()}</LanguageCode>
        <Arrow isOpen={isOpen}>▼</Arrow>
      </SelectorButton>
      {isOpen && (
        <Dropdown>
          {languages.map((lang) => (
            <LanguageOption
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              active={language === lang.code}
            >
              <Flag>{lang.flag}</Flag>
              <LanguageName>{lang.name}</LanguageName>
              {language === lang.code && <Check>✓</Check>}
            </LanguageOption>
          ))}
        </Dropdown>
      )}
    </Container>
  );
}

export default LanguageSelector;

const Container = styled.div`
  position: relative;
  margin-right: 15px;

  @media (max-width: 768px) {
    margin-right: 10px;
  }
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    background-color: rgba(255, 255, 255, 0.5);
    border-color: rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 12px;
    gap: 4px;
  }
`;

const Flag = styled.span`
  font-size: 18px;
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const LanguageCode = styled.span`
  font-weight: 600;
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const Arrow = styled.span`
  font-size: 10px;
  transition: transform 0.2s;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  @media (max-width: 480px) {
    font-size: 8px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  z-index: 10;
  overflow: hidden;

  @media (max-width: 480px) {
    min-width: 130px;
  }
`;

const LanguageOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${(props) =>
    props.active ? "rgba(169, 225, 255, 0.3)" : "white"};

  &:hover {
    background-color: rgba(169, 225, 255, 0.5);
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    gap: 8px;
  }
`;

const LanguageName = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "400")};

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Check = styled.span`
  color: #4caf50;
  font-weight: bold;
  font-size: 16px;
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;
